import { useState } from 'react';
import { BrowserView, isMobile, MobileView } from 'react-device-detect';
import {
  Button,
  ListGroup,
  Table,
  Stack,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import { ImNewTab } from 'react-icons/im';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  ComHubHeadersEnum,
  JobChat,
  WorkerModalParams,
} from '@company/types/comm-hub.type';
import DATE_FORMATS from '@constants/dateFormat';
import { formatTimeOrDateNow, formatWorkDateRange } from '@utils/DateUtils';
import { BASE_PATH } from '@utils/Environment';
import { useJobAssignmentStatus } from '@hooks/useJobAssignmentStatus';
import { useChatContext } from '@company/state/chatContext';
import { useCompanyContext } from '@company/state/companyContext';
import { IWorkerData } from '@th-types/worker.type';
import {
  calculateReadUnreadDelta,
  getMessageIcon,
  handleBatchMarkJobChatsAsRead,
} from '@utils/ComHubUtils';

import './styles.css';

interface JobMessagesTableProps {
  jobChats: JobChat[];
  onShowWorkerProfile?: (data: WorkerModalParams) => void;
  title?: string;
  showHeader?: boolean;
  onCounterUpdate: (delta: number) => void;
  headerCount?: number;
}

function JobMessagesTable({
  jobChats,
  onShowWorkerProfile,
  title,
  showHeader = true,
  onCounterUpdate,
  headerCount,
}: JobMessagesTableProps) {
  const { openChat, markAsRead, markAsUnread, batchMarkAsRead } =
    useChatContext();
  const { indicators } = useCompanyContext();
  const jobAssignmentStatus = useJobAssignmentStatus()(undefined);
  const { shouldShowWorkerInfoInChat } = jobAssignmentStatus;

  const headers = [
    ComHubHeadersEnum.EVENT_NAME,
    ComHubHeadersEnum.WORKER_NAME,
    ComHubHeadersEnum.POSITION,
    ComHubHeadersEnum.LOCATION,
    ComHubHeadersEnum.DATES_WORKED,
    ComHubHeadersEnum.ACTIONS_COLUMN,
  ];

  const newJobChats = jobChats?.filter((jobChat) => jobChat.unreadMessages > 0);

  const [isHovered, setIsHovered] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState<number | null>(null);

  const handleMarkAsRead = (chats: JobChat[]) => {
    const delta = calculateReadUnreadDelta(
      chats,
      (chat) => chat.unreadMessages > 0
    );

    handleBatchMarkJobChatsAsRead(
      chats,
      batchMarkAsRead,
      markAsRead,
      markAsUnread,
      (message) => message.chatId,
      (message) => message.unreadMessages > 0,
      () => onCounterUpdate(delta)
    );
  };

  const handleOpenJobChat = (jobChat: JobChat) => {
    const worker = {
      id: jobChat.workerInfo.workerId,
      fullName: jobChat.workerInfo.workerFullName,
      profileImageUrl: jobChat.workerInfo.workerProfileImageUrl,
      cellPhone: jobChat.workerInfo.workerCellphone,
      email: jobChat.workerInfo.workerEmail,
    };
    openChat(
      worker as IWorkerData,
      jobChat.chatId,
      shouldShowWorkerInfoInChat(jobChat.jobAssignmentInfo),
      jobChat,
      () => {
        // Only update counter on successful API call
        if (jobChat.unreadMessages > 0) {
          onCounterUpdate(-1);
        }
      }
    );
  };

  return (
    <div className="th-table">
      {showHeader && (
        <>
          <header
            className="d-flex align-items-center flex-wrap"
            style={{ minHeight: '38px' }}
          >
            <Link
              to="?tab=job-messages"
              className="me-auto text-decoration-none align-self-end"
              style={{ color: 'inherit' }}
            >
              <h4 className="text-start">
                {title}{' '}
                {(headerCount !== undefined || indicators) && (
                  <b data-testid="new-counter">
                    ({headerCount ?? indicators?.unreadConversationsCount ?? 0})
                  </b>
                )}
              </h4>
            </Link>
            {newJobChats.length ? (
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Mark new messages on page as read</Tooltip>}
              >
                <Button
                  type="button"
                  variant="light"
                  className="ms-auto fw-bold d-flex align-items-center"
                  style={{ fontSize: isMobile ? '12px' : '14px' }}
                  onClick={() => {
                    handleMarkAsRead(newJobChats);
                  }}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  {newJobChats.length} new
                  {getMessageIcon(true, isHovered, isMobile)}
                </Button>
              </OverlayTrigger>
            ) : null}
          </header>
          <hr style={{ margin: '10px 0' }} />
        </>
      )}
      {jobChats?.length ? (
        <>
          <BrowserView>
            <Table>
              <thead>
                <tr>
                  {headers.map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* RENDER COLUMS BY ROW */}
                {jobChats.map((jobChat) => {
                  return (
                    <tr
                      key={jobChat.chatId}
                      className={`text-underline cursor-point ${
                        jobChat.unreadMessages ? 'unread-message' : ''
                      }`}
                      onMouseEnter={() => setHoveredMessageId(jobChat.chatId)}
                      onMouseLeave={() => setHoveredMessageId(null)}
                      onClick={() => handleOpenJobChat(jobChat)}
                    >
                      <td className="whitespace-nowrap">
                        {jobChat.jobAssignmentInfo.jobId ? (
                          <div className="d-flex align-items-center">
                            <Link
                              to={`${BASE_PATH}jobs/${jobChat.jobAssignmentInfo.jobId}`}
                              target="_blank"
                              className="cursor-point event-name-link"
                              style={{ color: 'currentcolor' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {jobChat.jobAssignmentInfo.eventName}
                            </Link>
                            <ImNewTab
                              color="var(--yellow-dark-20)"
                              className="event-name-link-icon"
                            />
                          </div>
                        ) : (
                          <span>{jobChat.jobAssignmentInfo.eventName}</span>
                        )}
                      </td>
                      <td style={{ width: '200px' }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              onShowWorkerProfile &&
                              jobChat.workerInfo.workerId
                            ) {
                              onShowWorkerProfile({
                                workerId: jobChat.workerInfo.workerId,
                                jobAssignmentId:
                                  jobChat.jobAssignmentInfo.jobAssignmentId,
                                jobId: jobChat.jobAssignmentInfo.jobId,
                              });
                            }
                          }}
                          className={`${jobChat.unreadMessages ? 'unread-message' : ''} worker-name-link text-capitalize text-truncate d-inline-block w-100 text-start`}
                        >
                          {jobChat.workerInfo.workerFullName}{' '}
                          {jobChat.messageCount > 1 && jobChat.messageCount}
                        </button>
                      </td>
                      <td>
                        <span className="event-position">
                          {jobChat.jobAssignmentInfo.position}
                        </span>
                      </td>
                      <td>
                        <span className="address">
                          {jobChat.jobAssignmentInfo.address?.googleAddress ||
                            'N/A'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap me-2">
                        {(jobChat.jobAssignmentInfo.start &&
                          jobChat.jobAssignmentInfo.end &&
                          formatWorkDateRange(
                            new Date(jobChat.jobAssignmentInfo.start),
                            new Date(jobChat.jobAssignmentInfo.end),
                            DATE_FORMATS.DATE_FORMAT_SHORT
                          )) ||
                          'N/A'}
                      </td>
                      {/* BUTTONS FOR ROW ACTIONS */}
                      <td className="actions text-end">
                        <div className="d-flex align-items-center justify-content-end h-100">
                          {hoveredMessageId === jobChat.chatId ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip>
                                  {!jobChat.hasMultipleAuthors
                                    ? 'Only you in this chat'
                                    : `Mark as ${
                                        jobChat.unreadMessages
                                          ? 'read'
                                          : 'unread'
                                      }`}
                                </Tooltip>
                              }
                            >
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkAsRead([jobChat]);
                                }}
                                disabled={!jobChat.hasMultipleAuthors}
                              >
                                {getMessageIcon(
                                  jobChat.unreadMessages > 0,
                                  hoveredMessageId === jobChat.chatId,
                                  isMobile
                                )}
                              </button>
                            </OverlayTrigger>
                          ) : (
                            <span className="date me-2">
                              {jobChat.lastMessageDateTime &&
                                formatTimeOrDateNow(
                                  jobChat.lastMessageDateTime,
                                  new Date(
                                    jobChat.lastMessageDateTime
                                  ).getFullYear() === new Date().getFullYear()
                                    ? DATE_FORMATS.MONTH_DAY_FORMAT
                                    : DATE_FORMATS.MONTH_DAY_YEAR_FORMAT
                                )}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </BrowserView>
          <MobileView>
            <ListGroup variant="flush">
              {jobChats.map((jobChat) => {
                return (
                  <ListGroup.Item
                    key={jobChat.chatId}
                    className={`ps-0 pe-0 border-0 d-flex gap-2 align-items-center ${
                      jobChat.unreadMessages ? 'unread-message' : ''
                    }`}
                    onClick={(e) => {
                      const dismissButton =
                        e.currentTarget.querySelector('.dismiss-button');
                      if (!dismissButton?.contains(e.target as Node)) {
                        handleOpenJobChat(jobChat);
                      }
                    }}
                  >
                    <Stack style={{ fontSize: '0.8rem' }}>
                      <span className="text-capitalize fw-bold fs-6">
                        {jobChat.workerInfo.workerFullName}{' '}
                        {jobChat.messageCount > 1 && jobChat.messageCount}
                      </span>
                      {jobChat.jobAssignmentInfo.jobId ? (
                        <Link
                          to={`${BASE_PATH}jobs/${jobChat.jobAssignmentInfo.jobId}`}
                          target="_blank"
                          className="cursor-point event-name-link"
                          style={{ color: 'currentcolor' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {jobChat.jobAssignmentInfo.eventName}
                        </Link>
                      ) : (
                        <span>{jobChat.jobAssignmentInfo.eventName}</span>
                      )}
                      <span className="date me-2">
                        {(jobChat.jobAssignmentInfo.start &&
                          jobChat.jobAssignmentInfo.end &&
                          formatWorkDateRange(
                            new Date(jobChat.jobAssignmentInfo.start),
                            new Date(jobChat.jobAssignmentInfo.end),
                            DATE_FORMATS.DATE_FORMAT_SHORT
                          )) ||
                          'N/A'}
                      </span>
                      <span>
                        {jobChat.jobAssignmentInfo.address?.city},{' '}
                        {
                          jobChat.jobAssignmentInfo.address?.state
                            ?.unnabreviated
                        }
                      </span>
                    </Stack>

                    <Stack className="ms-auto justify-content-around align-items-end">
                      <span className="text-nowrap">
                        {format(
                          new Date(jobChat.lastMessageDateTime),
                          DATE_FORMATS.SHORT_DATE_FORMAT
                        )}
                      </span>
                      {jobChat.hasMultipleAuthors && (
                        <Button
                          variant="light"
                          size="sm"
                          className="p-1 dismiss-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead([jobChat]);
                          }}
                        >
                          {getMessageIcon(
                            jobChat.unreadMessages > 0,
                            false,
                            isMobile
                          )}
                        </Button>
                      )}
                    </Stack>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </MobileView>
        </>
      ) : (
        <p className="mb-2 th-fs-14">No new notifications.</p>
      )}
    </div>
  );
}

export default JobMessagesTable;
